import { useEffect, useRef, useState } from "react";

import { attachmentsApi } from "../services/api";

const INTERNAL_IMAGE_RE = /^\/api\/attachments\/([0-9a-f-]{36})\/download$/i;
const SAFE_DATA_IMAGE_RE = /^data:image\/(?:png|jpe?g|gif|webp|bmp);base64,/i;
const BROWSER_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
]);   // no image/svg+xml: an SVG opened in a tab runs script in the app origin

const fallbackNode = (doc, attachment) => {
  const box = doc.createElement("span");
  box.className = "email-image-fallback";
  box.textContent = "לא ניתן להציג את התמונה ";
  if (attachment?.id) {
    const link = doc.createElement("a");
    link.href = "#";
    link.dataset.attachmentId = attachment.id;
    link.textContent = "פתיחת הקובץ";
    box.appendChild(link);
  }
  return box;
};

// Legacy presentational attributes that set colors outside of `style`. These aren't
// script vectors, but leaving them in place while the `style` attribute is stripped
// below lets emails apply a dark bgcolor/background without the matching light text
// color that made it readable, rendering the body invisible (dark-on-dark/black-on-dark).
const PRESENTATIONAL_COLOR_ATTRS = new Set(["bgcolor", "background", "color", "text", "link", "vlink", "alink"]);

// Schemes a link or image source may carry. Everything else with a scheme (javascript:, vbscript:, data: html, ...)
// is dropped; a value without a scheme (a relative path, "#anchor") has nothing to abuse.
const SAFE_URL_SCHEMES = new Set(["http", "https", "mailto", "tel", "cid", "blob", "data"]);

// A URL parser ignores ASCII tab / LF / CR anywhere in the input and strips leading/trailing C0 controls and spaces,
// so "java\nscript:..." and "\u0001javascript:..." are javascript: URLs once clicked. Compare the value the browser
// would see, not the raw attribute text.
// eslint-disable-next-line no-control-regex -- the control characters ARE the point
const urlAsParsed = (value) => value.replace(/[\u0000-\u0020\u007f-\u009f]/g, "").toLowerCase();

const hasUnsafeUrlScheme = (value) => {
  const scheme = /^([a-z][a-z0-9+.-]*):/.exec(urlAsParsed(value));
  return scheme !== null && !SAFE_URL_SCHEMES.has(scheme[1]);
};

const sanitizeDocument = (doc) => {
  // <source> (a <picture>'s srcset sibling) is removed outright rather than just having its
  // srcset attribute stripped below, since an empty <source> with no attributes left is inert
  // either way but removing it is simpler; <picture> itself is kept so any real <img> nested
  // inside it still renders — <picture> carries no src/srcset of its own.
  // <video>/<audio> fetch their src (and <video poster>, no src needed) the instant the sandboxed
  // iframe renders, the same silent-remote-fetch tracking-pixel problem the img/srcset/<picture>
  // handling above exists to prevent -- neither the attribute loop below (only href/src get a
  // scheme check, and http(s) is an explicitly allowed scheme) nor resolveInlineAttachmentImages
  // (only ever looks at <img>) does anything to stop it, so these are removed outright like the
  // other non-rendering-safe elements on this line; <track> (a <video>/<audio> child) goes with them.
  doc.querySelectorAll("script,iframe,object,embed,svg,math,form,input,button,meta,link,base,style,source,video,audio,track").forEach((node) => node.remove());
  doc.querySelectorAll("*").forEach((node) => {
    [...node.attributes].forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = urlAsParsed(attribute.value);
      const unsafeScriptScheme = (name === "href" || name === "src") && hasUnsafeUrlScheme(attribute.value);
      const unsafeData = (name === "href" || name === "src") && value.startsWith("data:") && !/^data:image\/(png|jpe?g|gif|webp|bmp);base64,/i.test(value);
      // srcset lets the browser prefer a remote URL over the resolved/blob-substituted src —
      // resolveInlineAttachmentImages only ever rewrites src, never srcset, so leaving it in
      // place would defeat the no-remote-image-loading goal the src rewrite exists for.
      // `ping` makes the browser itself POST a beacon to arbitrary attacker URLs as part of a link's
      // native click handling -- the same silent-remote-request tracking vector <video poster>/<source
      // srcset> are stripped to prevent above, except triggered by a click instead of render. It survives
      // for "#anchor" links in particular: interceptEmailLinks deliberately leaves those with no click
      // listener at all (they just scroll the email), so their native default action -- ping included --
      // still runs on click.
      if (name.startsWith("on") || name === "srcdoc" || name === "srcset" || name === "style" || name === "ping" || PRESENTATIONAL_COLOR_ATTRS.has(name) || unsafeScriptScheme || unsafeData) {
        node.removeAttribute(attribute.name);
      }
      if (node.tagName.toLowerCase() === "a" && name === "href" && /^(https?:|mailto:)/i.test(value)) node.setAttribute("rel", "noopener noreferrer");
    });
  });
};

const collapseWhitespace = (text) => text.replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();

// Decides whether the sanitized DOM actually has something worth showing in the
// iframe — as opposed to surviving-but-empty markup (bare wrapper divs, a lone <br>,
// &nbsp;, or Outlook/structural markup that sanitizeDocument strips down to nothing).
// Must run AFTER sanitizeDocument (and after resolveInlineAttachmentImages, so a
// successfully-resolved image counts and a failed one's fallback text still counts) —
// checking the raw HTML string beforehand can't see what sanitization actually removes
// wholesale (script/iframe/object/embed/svg/math/form/input/button/link/base/style all
// take their text content with them; a naive string tag-strip would leave that text
// behind as a false positive).
const hasRenderableSanitizedContent = (doc) => {
  const root = doc.body || doc.documentElement;
  if (!root) return false;
  if (root.querySelector("img")) return true;
  return collapseWhitespace(root.textContent || "").length > 0;
};

async function resolveInlineAttachmentImages(doc, attachments, getBlob) {
  const attachmentsById = new Map((attachments || []).map((attachment) => [String(attachment.id), attachment]));
  const blobUrls = [];

  await Promise.all([...doc.querySelectorAll("img")].map(async (image) => {
    const src = image.getAttribute("src") || "";
    const match = INTERNAL_IMAGE_RE.exec(src);
    if (!match) {
      if (!src.startsWith("blob:") && !SAFE_DATA_IMAGE_RE.test(src)) {
        image.replaceWith(fallbackNode(doc, null));
      }
      return;
    }

    const attachment = attachmentsById.get(match[1]);
    if (!attachment || !BROWSER_IMAGE_TYPES.has((attachment.content_type || "").toLowerCase())) {
      image.replaceWith(fallbackNode(doc, attachment || { id: match[1] }));
      return;
    }

    try {
      const blob = await getBlob(match[1]);
      const blobUrl = URL.createObjectURL(blob);
      blobUrls.push(blobUrl);
      image.src = blobUrl;
      image.dataset.attachmentId = match[1];
      image.alt = image.alt || attachment.filename || "תמונה מצורפת";
    } catch (_error) {
      image.replaceWith(fallbackNode(doc, attachment));
    }
  }));

  return blobUrls;
}

/**
 * Resolves `/api/attachments/{id}/download` image sources to authenticated
 * blob URLs and returns a plain HTML fragment (no document/style wrapper),
 * for direct use in a non-iframe container such as a comment body.
 */
export async function prepareSecureInlineHtml(html, attachments, getBlob = attachmentsApi.getBlob) {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  sanitizeDocument(doc);
  const blobUrls = await resolveInlineAttachmentImages(doc, attachments, getBlob);
  return { html: doc.body.innerHTML, blobUrls };
}

// What the image resolver actually reads from the list. Callers pass `data: attachments = []` from react-query,
// which is a NEW array on every render until the list has loaded (and again after each refetch): keying an effect
// on the array's identity rebuilt the iframe / re-fetched every image on every parent render (each keystroke in
// the composer or resolution note), so effects key on this signature and read the latest list from a ref.
export const attachmentsSignature = (attachments) =>
  (attachments || []).map((a) => `${a.id}:${a.content_type || ""}:${a.filename || ""}`).join("|");

export function SecureInlineHtml({ html, attachments = [], className }) {
  const [preparedHtml, setPreparedHtml] = useState("");
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  const attachmentsKey = attachmentsSignature(attachments);

  useEffect(() => {
    let active = true;
    let currentBlobUrls = [];
    prepareSecureInlineHtml(html, attachmentsRef.current).then((result) => {
      if (!active) {
        result.blobUrls.forEach(URL.revokeObjectURL);
        return;
      }
      currentBlobUrls = result.blobUrls;
      setPreparedHtml(result.html);
    });
    return () => {
      active = false;
      currentBlobUrls.forEach(URL.revokeObjectURL);
    };
  }, [html, attachmentsKey]);

  return <div className={className} dangerouslySetInnerHTML={{ __html: preparedHtml }} />;
}

export async function prepareSecureEmailHtml(html, attachments, getBlob = attachmentsApi.getBlob) {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  sanitizeDocument(doc);
  const blobUrls = await resolveInlineAttachmentImages(doc, attachments, getBlob);
  const hasRenderableContent = hasRenderableSanitizedContent(doc);

  const style = doc.createElement("style");
  style.textContent = `
    html,body{margin:0;padding:0;max-width:100%;overflow-wrap:anywhere;font-family:Arial,sans-serif;background:#fff;color:#111827}
    img{display:block;max-width:100%!important;height:auto!important;object-fit:contain;cursor:zoom-in}
    .email-image-fallback{display:inline-block;max-width:100%;padding:8px 10px;border:1px solid #e5e7eb;border-radius:6px;background:#f9fafb;color:#6b7280;font-size:13px}
    .email-image-fallback a{color:#2563eb;text-decoration:underline}
  `;
  doc.head.appendChild(style);
  return { html: `<!doctype html>${doc.documentElement.outerHTML}`, blobUrls, hasRenderableContent };
}

const EMAIL_BODY_FALLBACK_CLASSNAME = "px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed max-h-[350px] overflow-y-auto";

// The email body lives in a sandboxed iframe (no allow-popups / allow-top-navigation): a plain link click navigated the
// IFRAME to the external site, replacing the email (or showing a "refused to connect" page). Links are opened in a new
// tab from the parent instead (http(s) and mailto only; javascript: hrefs are already stripped by the sanitizer).
// Attachment links keep their own download handler.
export function interceptEmailLinks(doc, open = (href) => window.open(href, "_blank", "noopener,noreferrer")) {
  doc.querySelectorAll("a[href]").forEach((link) => {
    if (link.dataset.attachmentId) return;
    // "#section" links scroll inside the email itself: leave them alone
    if ((link.getAttribute("href") || "").trim().startsWith("#")) return;
    link.addEventListener("click", (event) => {
      event.preventDefault();
      const href = link.getAttribute("href") || "";
      if (/^(https?:|mailto:)/i.test(href.trim())) open(href.trim());
    });
  });
}

export function SecureEmailHtml({ html, attachments = [], title, fallbackText }) {
  // null = not ready yet. The iframe is never mounted with an empty srcDoc while
  // waiting on the async sanitize/resolve pass below — some browsers don't reliably
  // re-navigate an *already-mounted* iframe when its srcDoc attribute later changes
  // from "" to real content, leaving onLoad/resize stuck on the empty document (seen
  // in production: height stayed at 80px with an empty contentDocument). Gating the
  // iframe's very existence on preparedHtml means the first time it's ever in the DOM
  // is also the first time it has real content, so there's no empty-to-filled
  // transition for the browser to (possibly) miss.
  const [preparedHtml, setPreparedHtml] = useState(null);
  // Optimistic default: avoids flashing the fallback for a frame while the async
  // sanitize/resolve pass (below) is still running — matches the loading state
  // (nothing rendered yet, see preparedHtml above) rather than a fallback flash.
  const [hasRenderableContent, setHasRenderableContent] = useState(true);
  const [height, setHeight] = useState(80);
  const iframeRef = useRef(null);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  const attachmentsKey = attachmentsSignature(attachments);

  useEffect(() => {
    let active = true;
    let currentBlobUrls = [];
    setPreparedHtml(null);
    setHasRenderableContent(true);
    prepareSecureEmailHtml(html, attachmentsRef.current).then((result) => {
      if (!active) {
        result.blobUrls.forEach(URL.revokeObjectURL);
        return;
      }
      currentBlobUrls = result.blobUrls;
      setPreparedHtml(result.html);
      setHasRenderableContent(result.hasRenderableContent);
    });
    return () => {
      active = false;
      currentBlobUrls.forEach(URL.revokeObjectURL);
    };
  }, [html, attachmentsKey]);

  const handleLoad = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    interceptEmailLinks(doc);

    const resize = () => setHeight(Math.min(900, Math.max(80, doc.documentElement.scrollHeight, doc.body.scrollHeight)));
    doc.querySelectorAll("img[data-attachment-id]").forEach((image) => {
      image.addEventListener("load", resize, { once: true });
      image.addEventListener("error", () => {
        const attachment = attachments.find((item) => String(item.id) === image.dataset.attachmentId);
        image.replaceWith(fallbackNode(doc, attachment));
        resize();
      }, { once: true });
      image.addEventListener("click", (event) => {
        // an inline image can sit inside a link: open the image once, and neither bubble to the link's handler
        // (a second tab) nor let the iframe navigate
        event.preventDefault();
        event.stopPropagation();
        window.open(image.src, "_blank", "noopener,noreferrer");
      });
    });
    doc.querySelectorAll("a[data-attachment-id]").forEach((link) => {
      link.addEventListener("click", (event) => {
        event.preventDefault();
        const attachment = attachments.find((item) => String(item.id) === link.dataset.attachmentId);
        attachmentsApi.download(link.dataset.attachmentId, attachment?.filename || "attachment");
      });
    });
    resize();
  };

  if (!hasRenderableContent) {
    const trimmedFallback = (fallbackText || "").trim();
    return (
      <div dir="auto" className={EMAIL_BODY_FALLBACK_CLASSNAME}>
        {trimmedFallback || <span className="italic text-gray-400">(ללא תוכן)</span>}
      </div>
    );
  }

  // Still preparing — render nothing rather than an iframe with a blank srcDoc.
  if (preparedHtml === null) {
    return null;
  }

  return (
    <iframe
      ref={iframeRef}
      srcDoc={preparedHtml}
      className="w-full border-0"
      style={{ height: `${height}px`, maxWidth: "100%" }}
      sandbox="allow-same-origin"
      onLoad={handleLoad}
      title={title}
    />
  );
}

export function AttachmentThumbnail({ attachment }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const supported = BROWSER_IMAGE_TYPES.has((attachment.content_type || "").toLowerCase());

  useEffect(() => {
    if (!supported) return undefined;
    let active = true;
    let blobUrl;
    attachmentsApi.getBlob(attachment.id).then((blob) => {
      if (!active) return;
      blobUrl = URL.createObjectURL(blob);
      setUrl(blobUrl);
    }).catch(() => active && setFailed(true));
    return () => {
      active = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [attachment.id, supported]);

  if (!supported || failed) {
    return <span className="flex h-12 w-12 flex-none items-center justify-center rounded border bg-gray-50 text-[10px] text-gray-400">אין תצוגה</span>;
  }
  return url ? (
    <img src={url} alt={attachment.filename} className="h-12 w-12 flex-none rounded border object-contain bg-white" onError={() => setFailed(true)} />
  ) : (
    <span className="h-12 w-12 flex-none animate-pulse rounded border bg-gray-100" aria-label="טוען תצוגה מקדימה" />
  );
}
